import { IFormJBPHPContactRoles } from 'legacy/lut/classes/models/partner360/IFormHPContacts'
import { getJbpHpContactRolesCrud } from 'legacy/lut/components/widgets/public/partner360/helpers/hpContactRoles/getJbpHpContactRolesCrud'
import { useCallback, useEffect, useState } from 'react'

import { Logic, Operator } from 'core-components/types'
import MeasureController from 'core/analytics/MeasureController'
import { useMeasureController } from 'core/analytics/useMeasureController'

interface IUseHPContactsAndPartnerRolesParameters {
  partnerId: string
  metrics: MeasureController
}

export const useHpContactsRoles = ({
  partnerId,
  metrics,
}: IUseHPContactsAndPartnerRolesParameters) => {
  /** -------------------- Variables ----------------------- */
  const [isLoading, setIsLoading] = useState(false)
  const [partnerRoles, setPartnerRoles] = useState<IFormJBPHPContactRoles[]>([])

  const partnerRolesCrud = getJbpHpContactRolesCrud()
  const { startLoadData, stopLoadData } = useMeasureController(metrics)

  /** -------------------- Methods ----------------------- */
  const fetchPartnerRoles = useCallback(
    async (force?: boolean) => {
      startLoadData()

      const response = await partnerRolesCrud.fetch({ force })
      const sortedItems = [...response.items].sort(
        (a: IFormJBPHPContactRoles, b: IFormJBPHPContactRoles) =>
          (a.SortOrder ?? Number.MAX_SAFE_INTEGER) - (b.SortOrder ?? Number.MAX_SAFE_INTEGER)
      )
      setPartnerRoles(sortedItems)

      stopLoadData()

      return response.items
    },
    [startLoadData, stopLoadData, partnerRolesCrud]
  )

  /** -------------------- Effects ----------------------- */
  useEffect(() => {
    partnerRolesCrud.setFilter({
      logic: Logic.AND,
      filters: [
        {
          field: 'PartnerId',
          operator: Operator.EQUAL,
          value: partnerId,
        },
      ],
    })

    setIsLoading(true)
    fetchPartnerRoles().finally(() => {
      setIsLoading(false)
    })

    return () => {
      setPartnerRoles([])
    }
  }, [partnerRolesCrud, partnerId, fetchPartnerRoles])

  return {
    isLoading,
    partnerRoles,
    partnerRolesCrud,
    fetchPartnerRoles,
  }
}
